import {AxiosError} from "axios";
import {useClient} from "../../../utils/hooks";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {AnalyticsEvent} from "../../../utils/analytics";
import {message} from "antd";
import {
  PublicProfileLiveClassApiResponseEventCohost,
  TLiveClassApiResponse,
} from "../live-classes";
import {PaginationMeta} from "../../../redux/service/i-service";
import {
  normalizeV2AddCoHostResponse,
  normalizeV2CoHostedEventsResponse,
  normalizeV2CoHostedEventSingleResponse,
  normalizeV2FetchCoHostsResponse,
  normalizeV2ValidateCohostResponse,
} from "./co-host-normalizer";

export type ValidateCohostApiResponse = {
  profile_exists: boolean;
  profile_id: number;
};

export type GetCoHostsApiResponse = {
  coHosts: {
    // cohostExist: string;
    id: number;
    user_id: number;
    host_id: number | null;
    event_id: number;
    createdAt: string;
    updatedAt: string;
    first_name: string;
    last_name: string;
    email_address: string;
    status_id: number;
    status_name: string;
    host: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      onboarding_reminder: number;
      country: null;
      password: null;
      statusId: number;
      isSocialAuth: boolean;
      avatar: string | null;
      cover_image: string | null;
      createdAt: string;
      updatedAt: string;
    } | null;
  }[];
};

export type AddCoHostApiResponse = {
  coHost: {
    user_id: number;
    host_id: string;
    event_id: string;
    id: number;
    createdAt: string;
    updatedAt: string;
  };
};

export type GetCoHostedEvents = TLiveClassApiResponse & {
  hostProfile: PublicProfileLiveClassApiResponseEventCohost;
};

export const useAddCoHost = () => {
  const queryClient = useQueryClient();
  const client = useClient("CO_HOST");

  return useMutation({
    mutationFn: async (cohostData: {
      host_id: number | string | null;
      event_id: string;
      email_address: string;
      first_name: string;
      last_name: string;
    }) => {
      const payload = {
        hostId: cohostData.host_id,
        eventId: Number(cohostData.event_id),
        emailAddress: cohostData.email_address,
        firstName: cohostData.first_name,
        lastName: cohostData.last_name,
      };
      const res = await client(`co-host/add`, {method: "POST", data: payload});
      return normalizeV2AddCoHostResponse(res?.data ?? res);
    },

    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Co hosts", "Co host create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Co hosts", "Co host create success");
    },
    onSettled: (data, error, values) =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: ["co_hosts", String(values.event_id)]}),
      ]),
  });
};

export const useValidateCohost = (cohostEmail: string, triggerFetch: boolean) => {
  const client = useClient("CO_HOST");

  return useQuery<
    ValidateCohostApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["validate_co_host", cohostEmail],
    queryFn: async () => {
      const data = await client(`co-host/email/validate/${cohostEmail}`);
      const inner = data?.data ?? data;
      return normalizeV2ValidateCohostResponse(inner);
    },
    refetchOnWindowFocus: false,
    enabled: !!cohostEmail && triggerFetch,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFetchCoHosts = (event_id?: string) => {
  const client = useClient("CO_HOST");

  return useQuery<GetCoHostsApiResponse, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["co_hosts", event_id],
    queryFn: async () => {
      const data = await client(`co-host/fetch-all/${event_id}`);
      const inner = data?.data ?? data;
      return normalizeV2FetchCoHostsResponse(inner);
    },
    refetchOnWindowFocus: false,
    enabled: !!event_id,
  });
};

export const useGetCoHostedEvents = (
  filter: "upcoming" | "past",
  per_page: number = 5
) => {
  const client = useClient("CO_HOST");

  return useInfiniteQuery<
    {data: GetCoHostedEvents[]; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["co_hosted_live_classes", filter, per_page],
    queryFn: async ({pageParam = 1}) => {
      const path = `co-host/live-events/${filter}?page=${pageParam}&per_page=${per_page}`;
      const response = await client(path);
      const inner = response?.data ?? response;
      return normalizeV2CoHostedEventsResponse(inner, pageParam, per_page);
    },
    refetchInterval: 300000,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.meta) {
        return lastPage.meta.next_page === 0 ? undefined : lastPage.meta.next_page;
      } else {
        return undefined;
      }
    },
  });
};

type CoHostedEventQueryError = AxiosError<{status: boolean; message: string}>;

export const useGetCoHostedEvent = (eventId: string | number | undefined) => {
  const client = useClient("CO_HOST");

  return useQuery<GetCoHostedEvents, CoHostedEventQueryError>({
    queryKey: ["co_hosted_live_event", eventId],
    queryFn: async () => {
      const response = await client(`co-host/live-events/single/${eventId}`);
      const inner = response?.data ?? response;
      return normalizeV2CoHostedEventSingleResponse(inner);
    },
    refetchOnWindowFocus: false,
    enabled: !!eventId,
  });
};

export const useDeleteCoHost = () => {
  const queryClient = useQueryClient();
  const client = useClient("CO_HOST");

  return useMutation({
    mutationFn: (payload: {event_id: string; model_id: string}) =>
      client(`co-host/${payload.model_id}`, {method: "DELETE"}),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("CoHost", "CoHost delete fail");
    },
    onSuccess: () => {
      AnalyticsEvent("CoHost", "CoHost delete success");
    },
    onSettled: (data, error, values) =>
      queryClient.invalidateQueries({
        queryKey: ["co_hosts", String(values.event_id)],
      }),
  });
};

export const useResendCoHostInvite = () => {
  const client = useClient("CO_HOST");
  return useMutation({
    mutationFn: (cohostId: string) =>
      client(`co-host/resend-invitation/${cohostId}`, {method: "GET"}),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("CoHost", "CoHost resend invite fail");
    },
    onSuccess: () => {
      AnalyticsEvent("CoHost", "CoHost resend invite success");
    },
  });
};
